/**
 * engine/mobile.ts — Mobile Platform Hardening
 *
 * Every API that makes a phone feel like a native app instead of a web page
 * lives here. Wake Lock so the screen doesn't dim mid-game. Visual Viewport
 * tracking so the virtual keyboard doesn't eat the UI. Android back button
 * interception so "back" means "close this panel" not "leave the game."
 * Screen orientation awareness. Standalone PWA detection. Network status.
 * Reduced motion preference. Haptic feedback. Install prompt capture.
 *
 * All of these are progressive enhancements — if the API doesn't exist,
 * the game works fine without it. But when they're available, the game
 * becomes indistinguishable from native.
 *
 * Called once from main.ts during boot.
 */

import type { GameState } from "./state.js";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MobileState {
  /** Whether the app is running as an installed PWA (standalone/fullscreen) */
  isStandalone: boolean;
  /** Whether the device is online */
  isOnline: boolean;
  /** Whether the user prefers reduced motion */
  prefersReducedMotion: boolean;
  /** Current visual viewport height (tracks virtual keyboard appearance) */
  visualViewportHeight: number;
  /** Whether the virtual keyboard is likely open */
  keyboardOpen: boolean;
  /** Captured beforeinstallprompt event for deferred PWA install */
  installPrompt: Event | null;
  /** Whether wake lock is currently held */
  wakeLockActive: boolean;
}

// ─── Module State ───────────────────────────────────────────────────────────

let wakeLock: WakeLockSentinel | null = null;
let mobileState: MobileState | null = null;

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize all mobile platform integrations. Call once at boot.
 * Returns the mobile state object which is attached to GameState.
 */
export function initMobilePlatform(state: GameState): MobileState {
  const ms: MobileState = {
    isStandalone: detectStandalone(),
    isOnline: navigator.onLine,
    prefersReducedMotion: detectReducedMotion(),
    visualViewportHeight: window.innerHeight,
    keyboardOpen: false,
    installPrompt: null,
    wakeLockActive: false,
  };

  mobileState = ms;

  // ── Network status ──────────────────────────────────────────────────────
  window.addEventListener("online", () => { ms.isOnline = true; }, { passive: true });
  window.addEventListener("offline", () => { ms.isOnline = false; }, { passive: true });

  // ── Reduced motion — live query, updates if user changes system pref ────
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  motionQuery.addEventListener("change", (e) => {
    ms.prefersReducedMotion = e.matches;
  });

  // ── Visual Viewport API — track virtual keyboard ───────────────────────
  if (window.visualViewport) {
    const vv = window.visualViewport;
    const baseHeight = window.innerHeight;

    const onViewportResize = () => {
      ms.visualViewportHeight = vv.height;
      // Keyboard is "open" if viewport shrunk by more than 150px
      // (accounts for address bar changes which are smaller)
      ms.keyboardOpen = (baseHeight - vv.height) > 150;
    };

    vv.addEventListener("resize", onViewportResize, { passive: true });
    vv.addEventListener("scroll", onViewportResize, { passive: true });
  }

  // ── Android back button / gesture via History API ──────────────────────
  initBackButton(state);

  // ── PWA install prompt capture ─────────────────────────────────────────
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    ms.installPrompt = e;
  });

  // ── Wake Lock — keep screen alive during gameplay ──────────────────────
  requestWakeLock(ms);

  // Re-acquire wake lock when tab becomes visible again
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && ms.wakeLockActive === false) {
      requestWakeLock(ms);
    }
  });

  // ── Service Worker registration ────────────────────────────────────────
  registerServiceWorker();

  return ms;
}

/**
 * Get the current mobile state (for systems that need to check reduced motion, etc.)
 */
export function getMobileState(): MobileState | null {
  return mobileState;
}

/**
 * Trigger haptic feedback if available. Intensity: "light" | "medium" | "heavy"
 */
export function haptic(intensity: "light" | "medium" | "heavy" = "light"): void {
  if (!navigator.vibrate) return;
  switch (intensity) {
    case "light":  navigator.vibrate(10); break;
    case "medium": navigator.vibrate(25); break;
    case "heavy":  navigator.vibrate([30, 10, 30]); break;
  }
}

/**
 * Release wake lock (call on cleanup or when app backgrounding for extended time).
 */
export function releaseWakeLock(): void {
  if (wakeLock) {
    wakeLock.release().catch(() => {});
    wakeLock = null;
    if (mobileState) mobileState.wakeLockActive = false;
  }
}

/**
 * Show the deferred PWA install prompt if available.
 * Returns true if the user accepted the install.
 */
export async function showInstallPrompt(): Promise<boolean> {
  if (!mobileState?.installPrompt) return false;
  const prompt = mobileState.installPrompt as BeforeInstallPromptEvent;
  prompt.prompt();
  const result = await prompt.userChoice;
  mobileState.installPrompt = null;
  return result.outcome === "accepted";
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

function detectStandalone(): boolean {
  // iOS standalone
  if ((navigator as NavigatorStandalone).standalone === true) return true;
  // Android / desktop PWA
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.matchMedia("(display-mode: fullscreen)").matches) return true;
  return false;
}

function detectReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

async function requestWakeLock(ms: MobileState): Promise<void> {
  if (!("wakeLock" in navigator)) return;
  try {
    wakeLock = await navigator.wakeLock.request("screen");
    ms.wakeLockActive = true;
    wakeLock.addEventListener("release", () => {
      ms.wakeLockActive = false;
      wakeLock = null;
    });
  } catch {
    // Wake lock denied — screen will dim normally. That's fine.
    ms.wakeLockActive = false;
  }
}

/**
 * Android back button interception via the History API.
 *
 * We push a dummy state on init, then listen for popstate. When the user
 * hits back, we close the topmost UI layer instead of navigating away.
 * After handling, we push the dummy state again so the next back press
 * also stays in-game.
 */
function initBackButton(state: GameState): void {
  // Push an initial history entry so popstate fires on back
  history.pushState({ hearth: true }, "");

  window.addEventListener("popstate", () => {
    // Try to close the topmost UI layer
    const ui = state.ui;

    if (ui.deviceOpen) {
      // Close device overlay
      ui.deviceOpen = false;
      state.player.animState = ui.combatStance ? "combat_idle" : "idle";
    } else if (state.chat.inputOpen) {
      state.chat.inputOpen = false;
      state.chat.aeternaMode = false;
      const chatEl = document.getElementById("chat-input") as HTMLInputElement | null;
      if (chatEl) { chatEl.blur(); chatEl.style.display = "none"; }
    } else if (ui.furnitureContextId) {
      ui.furnitureContextId = null;
    } else if (ui.movingFurnitureId) {
      ui.movingFurnitureId = null;
      ui.movingOriginalTile = null;
      ui.placingFurnitureType = null; // always paired with moving
    } else if (ui.placingFurnitureType) {
      ui.placingFurnitureType = null;
    } else if (ui.inventoryOpen) {
      ui.inventoryOpen = false;
    } else if (ui.editMode) {
      ui.editMode = false;
      ui.selectedFurniture = null;
      ui.furnitureContextId = null;
      ui.pendingBuildAction = null;
    } else if (ui.combatStance) {
      ui.combatStance = false;
      state.player.animState = "idle";
    } else if (ui.aeternaOpen) {
      ui.aeternaOpen = false;
    } else {
      // Nothing to close — let the browser handle it (exit app)
      // Don't re-push, allow natural navigation
      return;
    }

    // Re-push so the next back press also stays in-game
    history.pushState({ hearth: true }, "");
  });
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  // Only register in production or when served over HTTP (not file://)
  if (location.protocol === "file:") return;

  navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
    // SW registration failed — the game works fine without it.
    // Most likely cause: localhost without HTTPS, or browser doesn't support SW.
  });
}

// ─── Type Augmentations ──────────────────────────────────────────────────────

interface NavigatorStandalone extends Navigator {
  standalone?: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface WakeLockSentinel extends EventTarget {
  readonly released: boolean;
  readonly type: "screen";
  release(): Promise<void>;
}
